import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useParams } from "react-router-dom";

import {
  api,
  getPosHeaders,
  getPosSession,
} from "./api";

const PermissionContext = createContext(null);

function getErrorMessage(error) {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") {
    return detail;
  }

  if (Array.isArray(detail)) {
    return detail
      .map((item) => item.msg)
      .join(". ");
  }

  return "No fue posible validar los permisos del POS.";
}

export function PermissionProvider({ children }) {
  const { company } = useParams();

  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [role, setRole] = useState("");
  const [error, setError] = useState("");
  const [loadingPermissions, setLoadingPermissions] =
    useState(true);

  const refreshPermissions = useCallback(
    async (signal) => {
      setUser(null);
      setPermissions([]);
      setRole("");
      setError("");

      if (!company || !getPosSession(company)?.access_token) {
        setLoadingPermissions(false);
        return;
      }

      setLoadingPermissions(true);

      try {
        const config = {
          headers: getPosHeaders(company),
          signal,
        };

        const [userResponse, permissionsResponse] =
          await Promise.all([
            api.get("/auth/pos/me", config),
            api.get("/auth/pos/permissions", config),
          ]);

        if (signal?.aborted) return;

        const currentUser = userResponse.data;
        const permissionData = permissionsResponse.data;

        if (currentUser?.subdomain !== company) {
          throw new Error(
            "La sesión POS no corresponde a esta empresa."
          );
        }

        if (!Array.isArray(permissionData?.permissions)) {
          throw new Error(
            "La respuesta de permisos POS debe incluir un arreglo permissions."
          );
        }

        setUser(currentUser);

        setPermissions(
          permissionData.permissions.filter(
            (permission) => typeof permission === "string"
          )
        );

        setRole(
          permissionData.role || currentUser.role || ""
        );
      } catch (requestError) {
        if (signal?.aborted) return;

        setUser(null);
        setPermissions([]);
        setRole("");

        setError(
          requestError.response
            ? getErrorMessage(requestError)
            : requestError.message ||
                "No fue posible validar la sesión POS."
        );
      } finally {
        if (!signal?.aborted) {
          setLoadingPermissions(false);
        }
      }
    },
    [company]
  );

  useEffect(() => {
    const controller = new AbortController();

    refreshPermissions(controller.signal);

    return () => controller.abort();
  }, [refreshPermissions]);

  // Esta función se puede usar directamente como onClick.
  const handleRefreshPermissions = useCallback(
    () => refreshPermissions(),
    [refreshPermissions]
  );

  const value = useMemo(
    () => ({
      user,
      permissions,
      role,
      error,
      loadingPermissions,
      refreshPermissions: handleRefreshPermissions,

      can: (permissionCode) =>
        role === "superadmin" ||
        permissions.includes(permissionCode),
    }),
    [
      user,
      permissions,
      role,
      error,
      loadingPermissions,
      handleRefreshPermissions,
    ]
  );

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionContext);

  if (!context) {
    throw new Error(
      "usePermissions debe usarse dentro de PermissionProvider."
    );
  }

  return context;
}